import {
  Trash2,
  DollarSign,
  AlertTriangle,
  Lightbulb,
} from 'lucide-react';
import { GenAIModal } from '@/components/ui/genai-modal';
import type { WasteAnalysisResult } from './types';

interface WasteAnalysisModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onAnalysisComplete: (result: WasteAnalysisResult) => void;
  readonly venueName?: string;
}

export function WasteAnalysisModal({
  isOpen,
  onClose,
  onAnalysisComplete,
  venueName,
}: WasteAnalysisModalProps) {
  const contextBuilder = () => ({
    venueName,
    timestamp: new Date().toISOString(),
  });

  const renderPreview = (result: WasteAnalysisResult) => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Trash2 className="h-5 w-5 text-green-600" />
        <span className="font-medium text-green-800">Waste Analysis Complete</span>
      </div>
      <p className="text-sm text-green-700">{result.summary}</p>
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-700">
          <AlertTriangle className="h-3 w-3" />
          {result.items.length} items identified
        </span>
        <span className="flex items-center gap-1 px-2 py-1 rounded bg-red-100 text-red-700">
          <DollarSign className="h-3 w-3" />
          Est. cost: ${result.totalEstimatedCost.toFixed(2)}
        </span>
        <span className="px-2 py-1 rounded bg-amber-100 text-amber-700">
          {result.category}
        </span>
      </div>
      {result.recommendations.length > 0 && (
        <div className="flex items-start gap-2 text-xs text-green-600">
          <Lightbulb className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>{result.recommendations[0]}</span>
        </div>
      )}
      <div className="text-xs text-green-600">
        Items: {result.items.map((item) => `${item.itemName} (${item.estimatedQuantity}${item.unit})`).join(', ')}
      </div>
    </div>
  );

  return (
    <GenAIModal<WasteAnalysisResult>
      isOpen={isOpen}
      onClose={onClose}
      onComplete={onAnalysisComplete}
      promptKey="waste-analysis"
      title="AI Waste Analysis"
      description="Take a photo of food waste and AI will analyze and log it automatically"
      placeholder="Add any additional context about the waste (e.g., from lunch service, found in walk-in cooler)..."
      supportsImage
      contextBuilder={contextBuilder}
      renderPreview={renderPreview}
      submitLabel="Analyze Waste"
      successLabel="Log This Waste"
    />
  );
}
